import { FC } from 'react';
import uniq from 'lodash/fp/uniq';

import {
  Box,
  Center,
  Circle,
  HStack,
  Icon,
  StackDivider,
  Text,
  VStack,
} from '@chakra-ui/react';

import { UserAvatar } from '../user-avatar';
import { PeopleGroupFilled } from '../../assets';

import { Leader } from '../../types';

const getBgColor = (index: number) => {
  let color = 'red';
  switch (index) {
    case 0:
      color = '#ffd56b';
      break;
    case 1:
      color = '#cccccc';
      break;
    case 2:
      color = '#ca9215';
      break;

    case -1:
      color = 'white';
      break;

    default:
      color = 'white';
  }
  return color;
};

/**
 * Displays a rank number in a circle with a colored background and black border.
 *  A rank index of -1 indicates that a blank circle is rendered. Not visible but takes
 *  up the appropriate amount of space.
 */
const RankIcon = ({ rankIndex }: { rankIndex: number }): any => {
  const row = rankIndex + 1;
  return (
    <Circle size="24px" bgColor={rankIndex !== -1 ? 'black' : 'white'}>
      <Circle
        size="20px"
        bgColor={getBgColor(rankIndex)}
        color={rankIndex !== -1 ? 'black' : 'white'}
      >
        {row}
      </Circle>
    </Circle>
  );
};

export interface LeaderboardPanelProps {
  leaders: Leader[];
  isTeamTab: boolean;
}

/**
 * Construct a list of ranked leaders. Leaders with the same amount raised are ranked equally (a tie)
 */
const groupTiedLeaders = (leaders: Leader[]): Leader[][] => {
  if (leaders.length === 0) return [];

  // Get unique amounts
  const amounts: string[] = uniq(leaders.map((leader) => leader.amountRaised));

  // Group amounts that are tied
  const groupedLeaders: Leader[][] = [];
  amounts.forEach((amount) => {
    groupedLeaders.push(
      leaders.filter((leader) => leader.amountRaised === amount)
    );
  });
  // No sorting of tied leaders required, use the sort order provide by TR response.
  return groupedLeaders;
};

export const LeaderboardPanel: FC<LeaderboardPanelProps> = ({
  leaders,
  isTeamTab,
}) => (
  <VStack
    align="stretch"
    spacing={4}
    divider={<StackDivider color="brand.primary.darkGrey" />}
  >
    {groupTiedLeaders(leaders).map((group, rankIndex) => {
      const centerRow = Math.floor((group.length + 1) / 2);
      const isEvenRowCount = group.length % 2 === 0;

      return (
        <Box key={rankIndex} data-testid={`rank-${rankIndex}`}>
          {group.map((leader, index) => {
            const { name, amountRaised, avatar, url, numTeamMembers } = leader;
            const fullName = name;
            const row = index + 1;
            const showRank = row === centerRow;
            const rankIconOffset = isEvenRowCount ? '40px' : '0';
            const isRankedFirst = rankIndex === 0;
            const amountLine = isTeamTab
              ? `${numTeamMembers} members - ${amountRaised}`
              : amountRaised;
            return (
              <HStack
                alignItems="center"
                spacing={2}
                key={index}
                data-testid={`Individual-leaderboard-group-${rankIndex}-${index}`}
              >
                <Box
                  style={{
                    position: 'relative',
                    top: rankIconOffset,
                  }}
                >
                  {showRank && <RankIcon rankIndex={rankIndex} />}
                  {!showRank && <RankIcon rankIndex={-1} />}
                </Box>
                <Center minW="60px" minH={isRankedFirst ? '60px' : '40px'}>
                  <UserAvatar
                    size={isRankedFirst ? 'lg' : 'md'}
                    src={avatar}
                    name={name}
                    aria-label={`${name} Avatar - Header`}
                  />
                </Center>
                <Box>
                  <Text
                    maxW="200px"
                    title={fullName}
                    noOfLines={2}
                    textDecoration="underline"
                    color="black"
                    cursor="pointer"
                    onClick={() => {
                      window.open(url, '_blank');
                    }}
                  >
                    {fullName}
                  </Text>
                  <HStack minW="250px">
                    {isTeamTab && (
                      <Icon
                        as={PeopleGroupFilled}
                        boxSize="12px"
                        color="brand.additional.link"
                      />
                    )}
                    <Text fontSize="xs" color="brand.primary.darkGrey">
                      {amountLine}
                    </Text>
                  </HStack>
                </Box>
              </HStack>
            );
          })}
        </Box>
      );
    })}
  </VStack>
);
